import React, { useState, useEffect, memo } from "react";
import { FlatList } from "react-native";
import styled from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "@components/atoms";
import { CustomTopTabsProps } from "./types";
import { DEVICE_SIZE } from "@utils/index";
import { Platform } from "react-native";
import { Menu, MenuItem, MenuItemLabel } from "@gluestack-ui/themed";
import { Pressable } from "react-native";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import Animated from "react-native-reanimated";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import { css } from "styled-components/native";
import { VDBIcons } from "@utils/VDBIcons";
import { useNavigationState } from "@react-navigation/native";
import { getActiveRouteName } from "@utils/analytics";
import Routes from "@navigations/Routes";
import { useCoachOverlay } from "../../../hooks/useCoachOverlay";
import { CoachOverlayStep } from "@components/CoachOverlay";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useSearchFiltersAuxPayload } from "@redux/slices/searchFilters";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { DefaultTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";

const menuItemIconName = (viewType: string) => {
  switch (viewType) {
    case SearchResultsAndFavoritesEnum.GRID:
      return "grid";
    case SearchResultsAndFavoritesEnum.LIST:
      return "list";
    case SearchResultsAndFavoritesEnum.LIST_EXPANDED:
      return "list-expanded";
    case SearchResultsAndFavoritesEnum.VIDEO_ONLY:
      return "video";
    default:
      return "grid";
  }
};

const CustomTopTabs = ({
  productArray,
  handleCardClick,
  flatListBackgroundColor,
  menuOptions,
  defaultViewType,
  selectViewType,
  menuItemEventName,
  sortingFiltersParams,
  showDotOnFilter,
  isFilterVisible,
  disableAnimations,
  tabWidth,
  tabGap,
  shouldUseFlexGrow = true,
  hideBottomBorder = false,
  showChangeViewMenu,
  sectionName,
  totalItems = 0,
  fromSearchResultScreen = false,
}: CustomTopTabsProps) => {
  const [_, __, ___, _____, customTopTabBoxAnimatedStyle] = useScrollContext();
  const { theme, typography, t } = useScreenOptionsHooks();
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const userInfo = useUserInfo();
  const payload = useSearchFiltersAuxPayload();
  const [selectedViewType, setSelectedViewType] = useState(defaultViewType);
  const [internalProductArray, setInternalProductArray] =
    useState(productArray);
  const { deviceWidth } = useIsFoldable();
  const [menuItemOptionIcon, setMenuItemOptionIcon] = useState(
    selectedViewType
      ? menuItemIconName(selectedViewType as string)
      : menuItemIconName(SearchResultsAndFavoritesEnum.GRID)
  );
  const screenWidth = isTablet ? DEVICE_SIZE.width * 0.65 : deviceWidth;

  const internalHandleCardClick = (clickedItem: any) => {
    const updatedProductArray = internalProductArray.map((item: any) =>
      item.id === clickedItem.id
        ? { ...item, selection: true }
        : { ...item, selection: false }
    );
    setInternalProductArray(updatedProductArray);
    handleCardClick(clickedItem);
  };

  useEffect(() => {
    setInternalProductArray((prevArray: any[]) => {
      const selectedId = prevArray.find((item: any) => item.selection)?.id;
      return productArray.map((item: any) => ({
        ...item,
        selection: item.id === selectedId,
      }));
    });
  }, [productArray]);

  const ItemData = ({ item }: any) => {
    if (item.id === 999) return item.component;
    if (
      item.eventName === SearchResultsAndFavoritesEnum.SAVE_SEARCH &&
      userInfo.isIFrame()
    ) {
      return null;
    }
    if (
      !showChangeViewMenu &&
      item.eventName === SearchResultsAndFavoritesEnum.CHANGE_VIEW
    ) {
      return null;
    }
    return (
      <ItemContainer>
        <CardMain
          tabWidth={tabWidth}
          screenWidth={screenWidth}
          isFocused={
            item?.eventName === menuItemEventName
              ? false
              : item.selection ||
                (item?.eventName === SearchResultsAndFavoritesEnum.FILTERS &&
                  isFilterVisible)
          }
          size={internalProductArray?.length}
          onPress={
            item?.eventName !== menuItemEventName
              ? () => {
                  internalHandleCardClick(item);
                }
              : undefined
          }
        >
          {item?.eventName && item?.eventName === menuItemEventName ? (
            <Menu
              mt={Platform.OS === "ios" ? 10 : -20}
              py={0}
              selectionMode="single"
              marginStart={15}
              marginTop={theme.isLargeScreen ? 16 : 0}
              borderRadius={12}
              placement={
                internalProductArray?.length === 1 ? "bottom" : "bottom left"
              }
              backgroundColor={theme["core-01"]}
              trigger={({ ...triggerProps }) => {
                const isFocused = triggerProps["aria-expanded"];
                return (
                  <Pressable {...triggerProps}>
                    <CardMainMenu
                      screenWidth={screenWidth}
                      isFocused={isFocused}
                      size={internalProductArray?.length}
                    >
                      <Animated.View
                        style={
                          !disableAnimations ? customTopTabBoxAnimatedStyle : {}
                        }
                      >
                        <VDBIcon
                          color={
                            isFocused ? theme["core-05"] : theme["core-06"]
                          }
                          name={menuItemOptionIcon}
                          size={24}
                          variant={IconsVariant.VDBCustom}
                        />
                      </Animated.View>

                      <TextContainer>
                        <VDBText
                          type={typography["Minor-R"]}
                          color={
                            isFocused ? theme["core-05"] : theme["core-06"]
                          }
                          numberOfLines={1}
                        >
                          {t(item.title)}
                        </VDBText>
                      </TextContainer>
                    </CardMainMenu>
                  </Pressable>
                );
              }}
            >
              {menuOptions &&
                menuOptions.map((option, index) => (
                  <MenuItem
                    key={option.name}
                    textValue={option.label}
                    style={{ padding: 0, borderRadius: SpaceType.Basic }}
                    onPress={() => {
                      setSelectedViewType(option.name.toString());
                      selectViewType(option.name);
                      setMenuItemOptionIcon(
                        menuItemIconName(option.name.toString())
                      );
                    }}
                  >
                    <MenuItemParentContainer>
                      <MenuItemContainer
                        style={{
                          backgroundColor: theme["core-01"],

                          ...(index === menuOptions.length - 1 && {
                            borderBottomLeftRadius: SpaceType.Basic,
                            borderBottomRightRadius: SpaceType.Basic,
                          }),
                          ...(index === 0 && {
                            borderTopLeftRadius: SpaceType.Basic,
                            borderTopRightRadius: SpaceType.Basic,
                          }),
                        }}
                      >
                        <IconLabelWrapper>
                          <VDBIcon
                            color={
                              selectedViewType === option.name
                                ? theme["core-05"]
                                : theme["core-06"]
                            }
                            name={option.icon}
                            size={20}
                            variant={IconsVariant.VDBCustom}
                          />
                          <Spacer size={SpaceType.Basic} />
                          <MenuItemLabel size="sm" color={theme["core-05"]}>
                            <VDBText
                              type={typography["Minor-M"]}
                              color={theme["core-05"]}
                              numberOfLines={1}
                            >
                              {option.label}
                            </VDBText>
                          </MenuItemLabel>
                        </IconLabelWrapper>
                        {selectedViewType === option.name && (
                          <CheckIconWrapper>
                            <VDBIcon
                              color={theme["success-01"]}
                              name={VDBIcons.CHECK}
                              size={16}
                              variant={IconsVariant.VDBCustom}
                            />
                          </CheckIconWrapper>
                        )}
                      </MenuItemContainer>
                      {index < menuOptions.length - 1 && (
                        <LineView theme={theme} />
                      )}
                    </MenuItemParentContainer>
                  </MenuItem>
                ))}
            </Menu>
          ) : (
            <CardSubMenu>
              <Animated.View
                style={!disableAnimations ? customTopTabBoxAnimatedStyle : {}}
              >
                <VDBIcon
                  color={item.selection ? theme["core-05"] : theme["core-06"]}
                  name={item.icon}
                  size={24}
                  variant={IconsVariant.VDBCustom}
                />
                {((item?.eventName === SearchResultsAndFavoritesEnum.SORTING &&
                  sortingFiltersParams &&
                  sortingFiltersParams.length > 0) ||
                  (item?.eventName === SearchResultsAndFavoritesEnum.FILTERS &&
                    showDotOnFilter)) && <RedIconContainer theme={theme} />}
              </Animated.View>
              <TextContainer>
                <VDBText
                  type={typography["Minor-R"]}
                  color={item.selection ? theme["core-05"] : theme["core-06"]}
                >
                  {item.title}
                </VDBText>
              </TextContainer>
            </CardSubMenu>
          )}
        </CardMain>
      </ItemContainer>
    );
  };

  const newRouteName = useNavigationState((state) => getActiveRouteName(state));
  const isValidRoute =
    newRouteName === Routes.SearchResultScreen ||
    newRouteName === Routes.FilterSplitScreen;

  const { start: callOverlay, screenConfig } = useCoachOverlay({
    screen: "SearchResult",
    autoStart: false,
    enabled: isValidRoute,
  });

  if (fromSearchResultScreen && totalItems === 0) {
    return <></>;
  }

  return (
    <ParentContainer>
      <FlatList
        style={
          !hideBottomBorder && {
            backgroundColor: flatListBackgroundColor,
            borderColor: theme["core-03"],
            borderBottomWidth: 1,
          }
        }
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        data={internalProductArray}
        extraData={internalProductArray}
        onLayout={() => callOverlay()}
        contentContainerStyle={{
          ...(shouldUseFlexGrow ? { flexGrow: 1 } : {}),
          justifyContent: "center",
          alignItems: "center",
          maxHeight: 62,
          ...(theme.isLargeScreen
            ? {
                justifyContent: "flex-end",
                paddingHorizontal: 6,
                gap: tabGap ?? SpaceType.Double,
              }
            : {}),
        }}
        renderItem={({ item, index }) => {
          const stepConfig =
            screenConfig?.steps?.find(
              (step: any) => step.key === item.eventName
            ) || null;

          const copilotText = stepConfig ? stepConfig.text : "";
          const copilotOrder = stepConfig
            ? screenConfig?.steps?.indexOf(stepConfig)
            : index;
          if (
            userInfo.enableSellReturnButton() &&
            (payload[sectionName]?.with_available_items === "true" ||
              payload[sectionName]?.with_available_items === true) &&
            (item.eventName === SearchResultsAndFavoritesEnum.SAVE_SEARCH ||
              item.eventName === SearchResultsAndFavoritesEnum.FILTERS)
          ) {
            return null;
          }
          const isActiveStep =
            (item.eventName === "change_view" ||
              item.eventName === "save_search" ||
              item.eventName === "filters" ||
              item.eventName === "sorting") &&
            isValidRoute;

          return (
            <CoachOverlayStep
              screen="SearchResult"
              stepKey={item.eventName}
              text={copilotText}
              order={copilotOrder}
              active={isActiveStep}
            >
              <ItemData item={item} />
            </CoachOverlayStep>
          );
        }}
        keyExtractor={(item) => item.id.toString()}
      />
    </ParentContainer>
  );
};

const ParentContainer = styled.View`
  height: 62px;
`;

const RedIconContainer = styled.View<any>`
  position: absolute;
  align-self: flex-end;
  height: 8px;
  width: 8px;
  border-radius: 4px;
  border-width: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["error-01"]};
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["cont-00"]};
`;

const ItemContainer = styled.View`
  align-items: center;
  padding: 4px;
`;
const CardMain = styled.Pressable<any>`
  width: ${({
    screenWidth,
    size,
    tabWidth,
  }: {
    screenWidth: number;
    size: number;
    tabWidth: number;
  }) =>
    tabWidth
      ? tabWidth
      : screenWidth / (size === 1 ? 1 : size === 4 ? 4.5 : 4.5)}px;
  padding-bottom: 4px;
  padding-top: 4px;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    css`
      max-width: ${Platform.OS === "ios" ? "80px" : "92px"};
    `}

  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-content: flex-start;
  background-color: ${({
    theme,
    isFocused,
  }: {
    theme: DefaultTheme;
    isFocused: boolean;
  }) => (isFocused ? theme["core-02"] : theme["core-01"])};
  border-radius: 8px;
`;
const CardMainMenu = styled.View<any>`
  width: ${({
    screenWidth,
    size,
    theme,
  }: {
    screenWidth: number;
    size: number;
    theme: DefaultTheme;
  }) =>
    screenWidth /
    (size === 1 ? 1 : size === 4 ? (theme.isTablet ? 5.5 : 4.5) : 4.5)}px;
  padding: ${SpaceType.ExtraSmall}px;
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    Platform.OS === "web" && theme.isLargeScreen ? "row" : "column"};
  align-items: center;
  justify-content: center;
  background-color: ${({
    theme,
    isFocused,
  }: {
    theme: DefaultTheme;
    isFocused: boolean;
  }) => (isFocused ? theme["core-02"] : theme["core-01"])};
  border-radius: 8px;
  max-width: 110px;
  gap: ${SpaceType.ExtraSmall}px;
`;

const CardSubMenu = styled.View`
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  max-width: 110px;
  padding: ${SpaceType.ExtraSmall}px;
  gap: ${SpaceType.ExtraSmall}px;
`;

const TextContainer = styled.View``;

const LineView = styled.View<any>`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  width: auto;
`;
const IconLabelWrapper = styled.View`
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-05"]};
  flex-direction: row;
  align-items: center;
`;
const CheckIconWrapper = styled.View``;
const MenuItemContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 12px;
  width: 277px;
  justify-content: space-between;
`;
const MenuItemParentContainer = styled.View`
  width: 277px;
`;
export default memo(CustomTopTabs);
